import React, { useState, useEffect } from 'react';
import { 
  SpaceBetween,
  Container,
  Header,
  Box,
  ColumnLayout,
  TextContent,
  StatusIndicator,
  ExpandableSection,
  Button,
  Modal,
  Alert,
  Checkbox
} from '@cloudscape-design/components';
import { FullPageHeader } from './commons'
import { CodeView } from "@cloudscape-design/code-view";
import { submissionsApi } from 'api/Submission';
import { useAuth } from '../AuthContext';

const RequestStatus = {
  APPROVED: 'approved',
  PENDING: 'pending',
  REJECTED: 'rejected',
  ENDED: 'ended',
  ONGOING: 'ongoing'
};

const DetailField = ({ label, children }) => (
  <div>
    <Box margin={{ bottom: 'xxxs' }}>
      <TextContent>
        <span className="text-gray-600">{label}</span>
      </TextContent>
    </Box>
    <div>{children}</div>
  </div>
);

const PolicyStatement = ({ statement }) => (
  <Container>
    <SpaceBetween size="s">
      <DetailField label="Effect">
        <TextContent>
          <strong>{statement.Effect}</strong>
        </TextContent>
      </DetailField>
      <DetailField label="Actions">
        <ul className="m-0 pl-5">
          {Array.isArray(statement.Action) 
            ? statement.Action.map((action, idx) => <li key={idx}>{action}</li>)
            : <li>{statement.Action}</li>
          }
        </ul>
      </DetailField>
      <DetailField label="Resource">
        <TextContent>
          <code>{statement.Resource}</code>
        </TextContent>
      </DetailField>
    </SpaceBetween>
  </Container>
);

function formatDateTime(dateString) {
  return new Date(dateString).toLocaleString();
}

function getStatusType(status) {
  switch (status.toLowerCase()) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    case 'pending':
      return 'pending';
    case 'ongoing':
      return 'in-progress';
    case 'ended':
      return 'stopped';
    default:
      return 'info';
  }
}

const UserRequestDetails = ({ id }) => {
  const [requestData, setRequestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isApproveModalVisible, setApproveModalVisible] = useState(false);
  const [isRejectModalVisible, setRejectModalVisible] = useState(false);
  const [isRevokeModalVisible, setRevokeModalVisible] = useState(false);
  const [isCancelModalVisible, setCancelModalVisible] = useState(false);
  const [denyDelete, setDenyDelete] = useState(false);
  const { role } = useAuth();

  const handleAction = async (action) => {
    try {
      const options = {};
      if (action === 'approve' && denyDelete) {
        options.deny_delete = true;
      }
      await submissionsApi.performUserAccessAction(action, requestData.id, options);
      if (role !== 'admin') {
        window.location.href = '/my-applications';
      } else {
        fetchRequestDetails();
      }
    } catch (error) {
      console.error(`Error ${action}ing request:`, error);
    } finally {
      setApproveModalVisible(false);
      setRejectModalVisible(false);
      setRevokeModalVisible(false);
      setCancelModalVisible(false);
      setDenyDelete(false);
    }
  };

  const fetchRequestDetails = async () => {
    try {
      const response = await submissionsApi.getUserAccessDetails(id);
      setRequestData(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequestDetails();
  }, [id]);

  if (loading) {
    return <StatusIndicator type="loading">Loading request details...</StatusIndicator>;
  }

  if (error) {
    return <StatusIndicator type="error">{error}</StatusIndicator>;
  }

  const renderActions = () => {
    if (role === 'admin') {
      return (
        <SpaceBetween size="xs" direction="horizontal">
          <Button 
            variant="primary" 
            onClick={() => setRevokeModalVisible(true)}
            disabled={requestData?.status !== RequestStatus.ONGOING}
          >
            Revoke
          </Button>
          <Button 
            variant="primary" 
            onClick={() => setApproveModalVisible(true)}
            disabled={requestData?.status !== RequestStatus.PENDING}
          >
            Approve
          </Button>
          <Button 
            variant="primary" 
            onClick={() => setRejectModalVisible(true)}
            disabled={![RequestStatus.PENDING, RequestStatus.APPROVED].includes(requestData?.status)}
          >
            Reject
          </Button>
        </SpaceBetween>
      );
    } else {
      return (
        <SpaceBetween size="xs" direction="horizontal">
          <Button 
            variant="primary" 
            onClick={() => setRevokeModalVisible(true)}
            disabled={requestData?.status !== RequestStatus.ONGOING}
          >
            Revoke
          </Button>
          <Button 
            variant="primary" 
            onClick={() => setCancelModalVisible(true)}
            disabled={![RequestStatus.PENDING, RequestStatus.APPROVED].includes(requestData?.status)}
          >
            Cancel
          </Button>
        </SpaceBetween>
      );
    }
  };

  return (
    <>
      <SpaceBetween size="l">
        <FullPageHeader
          title={`Request: ${requestData.user_email}`}
          actions={renderActions()}
        />
        
        {role !== 'admin' && (
          <Alert type='info'>
            You can only have one active request per organization at a time. if you want to create a different request, please cancel/revoke the existing one.
          </Alert>
        )}
        
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <StatusIndicator type={getStatusType(requestData.status)}>
                  {requestData.status}
                </StatusIndicator>
              }
            >
              Basic Information
            </Header>
          }
        >
          <ColumnLayout columns={2} variant="text-grid">
            <SpaceBetween size="l">
              <Container
                header={
                  <Header variant="h3">Request Information</Header>
                }
              >
                <SpaceBetween size="s">
                  <DetailField label="Request ID">
                    <TextContent>
                      <code>{requestData.id}</code>
                    </TextContent>
                  </DetailField>
                  <DetailField label="Username">
                    {requestData.username}
                  </DetailField>
                  <DetailField label="User Email">
                    {requestData.user_email}
                  </DetailField>
                  <DetailField label="Target Account ID">
                    {requestData.target_account_id}
                  </DetailField>
                  <DetailField label="Target Account Name">
                    {requestData.target_account_name || 'Sub-Account'} 
                  </DetailField>
                  <DetailField label="Duration">
                    {requestData.duration} hours
                  </DetailField>
                </SpaceBetween>
              </Container>

              <Container
                header={
                  <Header variant="h3">Time Details</Header>
                }
              >
                <SpaceBetween size="s">
                  <DetailField label="Start Time">
                    {formatDateTime(requestData.start_datetime)}
                  </DetailField>
                  <DetailField label="End Time">
                    {formatDateTime(requestData.end_datetime)}
                  </DetailField>
                  <DetailField label="Created">
                    {formatDateTime(requestData.create_datetime)}
                  </DetailField>
                </SpaceBetween>
              </Container>
            </SpaceBetween>
          </ColumnLayout>
        </Container>

        <Container
          header={
            <Header variant="h2">Attached Policies</Header>
          }
        >
          <SpaceBetween size="l">
            {requestData.policies.map((policy, index) => (
              <ExpandableSection
                key={index}
                headerText={policy.title}
                headerDescription={`Type: ${policy.type}`}
              >
                <SpaceBetween size="l">
                  <CodeView
                    content={JSON.stringify(policy.json, null, 2)}
                  />
                </SpaceBetween>
              </ExpandableSection>
            ))}
          </SpaceBetween>
        </Container>

        {requestData.notes && (
          <Container
            header={
              <Header variant="h2">Notes</Header>
            }
          >
            <TextContent>
              <p>{requestData.notes}</p>
            </TextContent>
          </Container>
        )}
      </SpaceBetween>

      {/* Admin Action Modals */}
      {role === 'admin' && (
        <>
          <Modal
            visible={isApproveModalVisible}
            onDismiss={() => {
              setApproveModalVisible(false);
              setDenyDelete(false);
            }}
            header="Approve Request"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => {
                    setApproveModalVisible(false);
                    setDenyDelete(false);
                  }}>Cancel</Button>
                  <Button variant="primary" onClick={() => handleAction('approve')}>Approve</Button>
                </SpaceBetween>
              </Box>
            }
          >
            <SpaceBetween size="m">
              <p>Are you sure you want to approve this request?</p>
              <Checkbox
                checked={denyDelete}
                onChange={({ detail }) => setDenyDelete(detail.checked)}
              >
                Deny Delete
              </Checkbox>
            </SpaceBetween>
          </Modal>

          <Modal
            visible={isRejectModalVisible}
            onDismiss={() => setRejectModalVisible(false)}
            header="Reject Request"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => setRejectModalVisible(false)}>Cancel</Button>
                  <Button variant="primary" onClick={() => handleAction('reject')}>Reject</Button>
                </SpaceBetween>
              </Box>
            }
          >
            <p>Are you sure you want to reject this request?</p>
          </Modal>

          <Modal
            visible={isRevokeModalVisible}
            onDismiss={() => setRevokeModalVisible(false)}
            header="Revoke Request"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => setRevokeModalVisible(false)}>Cancel</Button>
                  <Button variant="primary" onClick={() => handleAction('revoke')}>Revoke</Button>
                </SpaceBetween>
              </Box>
            }
          >
            <p>Are you sure you want to revoke this session?</p>
          </Modal>
        </>
      )}

      {/* User Action Modals */}
      {role !== 'adminx' && (
        <>
          <Modal
            visible={isRevokeModalVisible}
            onDismiss={() => setRevokeModalVisible(false)}
            header="Revoke Request"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => setRevokeModalVisible(false)}>Cancel</Button>
                  <Button variant="primary" onClick={() => handleAction('revoke')}>Revoke</Button>
                </SpaceBetween>
              </Box>
            }
          >
            <p>Are you sure you want to revoke this session?</p>
          </Modal>

          <Modal
            visible={isCancelModalVisible}
            onDismiss={() => setCancelModalVisible(false)}
            header="Cancel Request"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => setCancelModalVisible(false)}>Cancel</Button>
                  <Button variant="primary" onClick={() => handleAction('cancel')}>Cancel Request</Button>
                </SpaceBetween>
              </Box>
            }
          >
            <p>Are you sure you want to reject this request?</p>
          </Modal>
        </>
      )}
    </>
  );
};

export default UserRequestDetails;